import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { 
  HelpCircle, BookOpen, FileText, Video, MessageCircle, 
  Search, ChevronRight, ExternalLink, Mail, Phone,
  Wrench, Ticket, Package, Warehouse, Settings, BarChart3,
  Users, MapPin, Calendar, QrCode
} from 'lucide-react';

const HelpPage = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('ghid');

  // Ghid rapid - sectiuni principale
  const quickGuide = [
    {
      title: 'Tichete Service',
      icon: Ticket,
      color: 'blue',
      description: 'Creaza si gestioneaza sesizarile de mentenanta',
      steps: [
        'Apasa butonul "TICHET NOU" din pagina Tichete',
        'Selecteaza sediul, locatia si echipamentul',
        'Descrie problema si selecteaza prioritatea',
        'Optional: adauga imagini pentru documentare',
        'Salveaza tichetul - va fi trimis catre manager'
      ],
      link: '/tickets'
    },
    {
      title: 'Revizii Preventive',
      icon: Wrench,
      color: 'green',
      description: 'Planifica si executa reviziile echipamentelor',
      steps: [
        'Acceseaza Mentenanta → Preventiva',
        'Creaza revizie noua sau selecteaza una existenta',
        'Completeaza diagnosticul si sarcinile',
        'Inregistreaza consumabilele utilizate',
        'Finalizeaza si genereaza raportul PDF'
      ],
      link: '/maintenance/preventive'
    },
    {
      title: 'WMS - Gestiune Stocuri',
      icon: Warehouse,
      color: 'purple',
      description: 'Gestioneaza stocurile de consumabile',
      steps: [
        'Acceseaza pagina WMS din meniu',
        'Vizualizeaza situatia stocurilor pe locatii',
        'Efectueaza receptii, eliberari sau transferuri',
        'Monitorizeaza alertele de stoc minim',
        'Exporta rapoarte pentru inventar'
      ],
      link: '/wms'
    },
    {
      title: 'Echipamente',
      icon: Package,
      color: 'orange',
      description: 'Administreaza nomenclatorul de echipamente',
      steps: [
        'Acceseaza Nomenclatoare → Echipamente',
        'Adauga echipament nou cu cod unic',
        'Asociaza cu sediul si locatia',
        'Defineste ierarhia (echipament parinte)',
        'Genereaza si printeaza cod QR'
      ],
      link: '/settings/equipment'
    },
    {
      title: 'Dashboard Configurabil',
      icon: BarChart3,
      color: 'cyan',
      description: 'Personalizeaza ecranul principal',
      steps: [
        'Apasa butonul "Configureaza" din Dashboard',
        'Selecteaza blocurile KPI dorite',
        'Reordoneaza cu sagetile sus/jos',
        'Configuratia se salveaza automat',
        'Vizualizeaza alertele si actiunile prioritare'
      ],
      link: '/dashboard'
    },
    {
      title: 'Scan QR',
      icon: QrCode,
      color: 'indigo',
      description: 'Acceseaza rapid echipamentele prin scanare',
      steps: [
        'Apasa "SCAN RAPID" din Dashboard',
        'Scaneaza codul QR al echipamentului',
        'Vizualizeaza detaliile si istoricul',
        'Creaza tichet sau revizie direct',
        'Acceseaza consumabilele din locatia BIN'
      ],
      link: '/scan'
    }
  ];

  // FAQ - Intrebari frecvente
  const faqItems = [
    {
      question: 'Cum creez un utilizator nou?',
      answer: 'Doar administratorii pot crea utilizatori. Acceseaza Admin → Utilizatori → UTILIZATOR NOU. Completeaza cei 4 pasi: Date personale, Rol, Acces locatii, Competente.'
    },
    {
      question: 'Cum transform un tichet in revizie?',
      answer: 'Deschide tichetul acceptat, apasa "Transforma in Revizie", selecteaza procedura si tehnicianul, apoi confirma. Tichetul va fi marcat ca "Transformat".'
    },
    {
      question: 'Cum export datele in Excel?',
      answer: 'Acceseaza Admin → Import/Export. Selecteaza tipul de date dorit (utilizatori, echipamente, etc.) si apasa Export. Fisierul Excel se va descarca automat.'
    },
    {
      question: 'Cum configurez alertele de stoc?',
      answer: 'Acceseaza Setari → Preferinte per Locatie. Selecteaza locatia, activeaza "Alarma stoc minim" si adauga adresele email pentru notificari.'
    },
    {
      question: 'Cum generez raportul PDF al unei revizii?',
      answer: 'Deschide revizia finalizata, acceseaza tab-ul "General" si apasa butonul "GENEREAZA PDF". Raportul include toate detaliile, sarcinile si costurile.'
    },
    {
      question: 'Cum adaug coordonate GPS pentru o locatie?',
      answer: 'Acceseaza Admin → Locatii, editeaza locatia dorita si completeaza campurile Latitudine si Longitudine. Locatia va aparea pe harta.'
    },
    {
      question: 'Cum functioneaza calculul SLA?',
      answer: 'SLA se calculeaza automat pe baza prioritatii tichetului: Critic (2h), Urgent (4h), Ridicat (8h), Mediu (24h), Scazut (48h). Statusul se actualizeaza in timp real.'
    },
    {
      question: 'Cum folosesc Quick Pick pentru consumabile?',
      answer: 'In pagina reviziei, tab-ul Consumabile, apasa "Quick Pick". Scaneaza codul BIN, selecteaza articolul si cantitatea. Stocul se actualizeaza automat.'
    }
  ];

  // Shortcuts de navigare
  const shortcuts = [
    { key: 'D', description: 'Dashboard', path: '/dashboard' },
    { key: 'T', description: 'Tichete', path: '/tickets' },
    { key: 'R', description: 'Revizii Preventive', path: '/maintenance/preventive' },
    { key: 'W', description: 'WMS Stocuri', path: '/wms' },
    { key: 'E', description: 'Echipamente', path: '/settings/equipment' },
    { key: 'S', description: 'Scan QR', path: '/scan' }
  ];

  // Filtrare FAQ
  const filteredFaq = faqItems.filter(item =>
    item.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.answer.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getColorClasses = (color) => {
    const colors = {
      blue: 'bg-blue-100 text-blue-600 border-blue-200',
      green: 'bg-green-100 text-green-600 border-green-200',
      purple: 'bg-purple-100 text-purple-600 border-purple-200',
      orange: 'bg-orange-100 text-orange-600 border-orange-200',
      cyan: 'bg-cyan-100 text-cyan-600 border-cyan-200',
      indigo: 'bg-indigo-100 text-indigo-600 border-indigo-200'
    };
    return colors[color] || colors.blue;
  };

  return (
    <div className="space-y-6" data-testid="help-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <HelpCircle className="w-7 h-7 text-[#0052CC]" />
            Centru de Ajutor
          </h1>
          <p className="text-slate-600 mt-1">Ghiduri, tutoriale si raspunsuri la intrebari frecvente</p>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-xl">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
        <Input
          placeholder="Cauta in documentatie..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 h-12"
          data-testid="help-search"
        />
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full max-w-lg grid-cols-4 h-11">
          <TabsTrigger value="ghid" className="flex items-center gap-2">
            <BookOpen className="w-4 h-4" />
            Ghid Rapid
          </TabsTrigger>
          <TabsTrigger value="faq" className="flex items-center gap-2">
            <MessageCircle className="w-4 h-4" />
            FAQ
          </TabsTrigger>
          <TabsTrigger value="shortcuts" className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Shortcut-uri
          </TabsTrigger>
          <TabsTrigger value="contact" className="flex items-center gap-2">
            <Phone className="w-4 h-4" />
            Contact
          </TabsTrigger>
        </TabsList>

        {/* Tab: Ghid Rapid */}
        <TabsContent value="ghid" className="mt-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {quickGuide.map((guide, index) => {
              const IconComponent = guide.icon;
              return (
                <Card 
                  key={index} 
                  className="card-industrial hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => navigate(guide.link)}
                >
                  <CardHeader className="pb-2">
                    <div className={`w-12 h-12 rounded-lg ${getColorClasses(guide.color)} flex items-center justify-center mb-2`}>
                      <IconComponent className="w-6 h-6" />
                    </div>
                    <CardTitle className="text-lg">{guide.title}</CardTitle>
                    <CardDescription>{guide.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ol className="space-y-2 text-sm text-slate-600">
                      {guide.steps.map((step, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <span className="w-5 h-5 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center text-xs font-medium flex-shrink-0 mt-0.5">
                            {i + 1}
                          </span>
                          <span>{step}</span>
                        </li>
                      ))}
                    </ol>
                    <Button variant="ghost" className="w-full mt-4 text-[#0052CC]">
                      Deschide <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* Tab: FAQ */}
        <TabsContent value="faq" className="mt-6">
          <div className="space-y-4 max-w-3xl">
            {filteredFaq.length > 0 ? filteredFaq.map((item, index) => (
              <Card key={index} className="card-industrial">
                <CardContent className="p-4">
                  <h3 className="font-semibold text-slate-900 flex items-center gap-2 mb-2">
                    <MessageCircle className="w-5 h-5 text-[#0052CC]" />
                    {item.question}
                  </h3>
                  <p className="text-slate-600 pl-7">{item.answer}</p>
                </CardContent>
              </Card>
            )) : (
              <Card className="card-industrial">
                <CardContent className="p-8 text-center text-slate-500">
                  <HelpCircle className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p>Nu am gasit rezultate pentru "{searchTerm}"</p>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Tab: Shortcuts */}
        <TabsContent value="shortcuts" className="mt-6">
          <Card className="card-industrial max-w-lg">
            <CardHeader>
              <CardTitle>Navigare Rapida</CardTitle>
              <CardDescription>Foloseste meniul din stanga sau acceseaza direct paginile</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {shortcuts.map((shortcut, index) => (
                  <div 
                    key={index}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 cursor-pointer"
                    onClick={() => navigate(shortcut.path)}
                  >
                    <div className="flex items-center gap-3">
                      <kbd className="px-2 py-1 bg-white border rounded text-sm font-mono">{shortcut.key}</kbd>
                      <span>{shortcut.description}</span>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-400" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="card-industrial max-w-lg mt-4">
            <CardHeader>
              <CardTitle>Versiune Aplicatie</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">Versiune:</span>
                  <Badge>v1.0.0</Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Ultima actualizare:</span>
                  <span>22.01.2026</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Mediu:</span>
                  <Badge variant="secondary">Productie</Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Contact */}
        <TabsContent value="contact" className="mt-6">
          <div className="grid md:grid-cols-2 gap-4 max-w-2xl">
            <Card className="card-industrial">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="w-5 h-5 text-[#0052CC]" />
                  Suport Email
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600 mb-4">Pentru intrebari sau probleme tehnice, contacteaza-ne:</p>
                <a 
                  href="mailto:suport@maintix.ro" 
                  className="text-[#0052CC] font-medium hover:underline flex items-center gap-2"
                >
                  suport@maintix.ro
                  <ExternalLink className="w-4 h-4" />
                </a>
              </CardContent>
            </Card>

            <Card className="card-industrial">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Phone className="w-5 h-5 text-[#0052CC]" />
                  Suport Telefonic
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600 mb-4">Program: Luni - Vineri, 08:00 - 17:00</p>
                <a 
                  href="tel:+40212345678" 
                  className="text-[#0052CC] font-medium hover:underline flex items-center gap-2"
                >
                  +40 21 234 5678
                  <ExternalLink className="w-4 h-4" />
                </a>
              </CardContent>
            </Card>
          </div>

          <Card className="card-industrial max-w-2xl mt-4">
            <CardHeader>
              <CardTitle>Raporteaza o Problema</CardTitle>
              <CardDescription>Descrie problema intampinata si te vom contacta in cel mai scurt timp</CardDescription>
            </CardHeader>
            <CardContent>
              <form className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Subiect</label>
                  <Input placeholder="ex: Eroare la generare PDF" className="h-11" />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Descriere</label>
                  <textarea 
                    className="w-full h-24 px-3 py-2 border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-[#0052CC]"
                    placeholder="Descrie problema in detaliu..."
                  />
                </div>
                <Button type="button" className="btn-primary" onClick={() => toast.success('Mesaj trimis! Te vom contacta in curand.')}>
                  Trimite
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default HelpPage;
